import { useEffect, useMemo, useState } from "react";
import { Box, Divider, Input, Typography } from "@mui/joy";
import { Button } from "../../../../../../shared";
import { useProject } from "../../../../../../hooks/query";

export function Insurance({ details }: { details: any | null }) {
    const projectId = details?.Id as string | undefined;
    const { useProjectAttributeUpdate } = useProject();
    const updateProjectAttributeSvc = useProjectAttributeUpdate();

    const [provider, setProvider] = useState<string>(details?.InsuranceProvider ?? "");
    const [claim, setClaim] = useState<string>(details?.InsuranceClaimNumber ?? "");
    const [policy, setPolicy] = useState<string>(details?.InsurancePolicyNumber ?? "");
    const [agentContact, setAgentContact] = useState<string>(details?.InsuranceAgentContact ?? "");
    const [agentNumber, setAgentNumber] = useState<string>(details?.InsuranceAgentNumber ?? "");
    const [agentEmail, setAgentEmail] = useState<string>(details?.InsuranceAgentEmail ?? "");
    const [adjusterContact, setAdjusterContact] = useState<string>(details?.AdjusterContact ?? "");
    const [adjusterEmail, setAdjusterEmail] = useState<string>(details?.AdjusterEmail ?? "");
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setProvider(details?.InsuranceProvider ?? "");
        setClaim(details?.InsuranceClaimNumber ?? "");
        setPolicy(details?.InsurancePolicyNumber ?? "");
        setAgentContact(details?.InsuranceAgentContact ?? "");
        setAgentNumber(details?.InsuranceAgentNumber ?? "");
        setAgentEmail(details?.InsuranceAgentEmail ?? "");
        setAdjusterContact(details?.AdjusterContact ?? "");
        setAdjusterEmail(details?.AdjusterEmail ?? "");
    }, [
        details?.InsuranceProvider,
        details?.InsuranceClaimNumber,
        details?.InsurancePolicyNumber,
        details?.InsuranceAgentContact,
        details?.InsuranceAgentNumber,
        details?.InsuranceAgentEmail,
        details?.AdjusterContact,
        details?.AdjusterEmail,
    ]);

    const hasChanges = useMemo(() => {
        return (
            (details?.InsuranceProvider ?? "") !== provider ||
            (details?.InsuranceClaimNumber ?? "") !== claim ||
            (details?.InsurancePolicyNumber ?? "") !== policy ||
            (details?.InsuranceAgentContact ?? "") !== agentContact ||
            (details?.InsuranceAgentNumber ?? "") !== agentNumber ||
            (details?.InsuranceAgentEmail ?? "") !== agentEmail ||
            (details?.AdjusterContact ?? "") !== adjusterContact ||
            (details?.AdjusterEmail ?? "") !== adjusterEmail
        );
    }, [details, provider, claim, policy, agentContact, agentNumber, agentEmail, adjusterContact, adjusterEmail]);

    const handleUpdate = async () => {
        if (!projectId || !hasChanges) return;
        setSaving(true);
        try {
            const updates: Array<{ Attribute: string; Value: string }> = [];
            if ((details?.InsuranceProvider ?? "") !== provider)
                updates.push({ Attribute: "InsuranceProvider", Value: provider });
            if ((details?.InsuranceClaimNumber ?? "") !== claim)
                updates.push({ Attribute: "InsuranceClaimNumber", Value: claim });
            if ((details?.InsurancePolicyNumber ?? "") !== policy)
                updates.push({ Attribute: "InsurancePolicyNumber", Value: policy });
            if ((details?.InsuranceAgentContact ?? "") !== agentContact)
                updates.push({ Attribute: "InsuranceAgentContact", Value: agentContact });
            if ((details?.InsuranceAgentNumber ?? "") !== agentNumber)
                updates.push({ Attribute: "InsuranceAgentNumber", Value: agentNumber });
            if ((details?.InsuranceAgentEmail ?? "") !== agentEmail)
                updates.push({ Attribute: "InsuranceAgentEmail", Value: agentEmail });
            if ((details?.AdjusterContact ?? "") !== adjusterContact)
                updates.push({ Attribute: "AdjusterContact", Value: adjusterContact });
            if ((details?.AdjusterEmail ?? "") !== adjusterEmail)
                updates.push({ Attribute: "AdjusterEmail", Value: adjusterEmail });

            for (const u of updates) {
                await updateProjectAttributeSvc.mutateAsync({ Id: projectId, Attribute: u.Attribute, Value: u.Value });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={2}>
                <Typography level="title-sm">Insurance</Typography>
                <Typography level="body-sm">Manage insurance details associated with this project.</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Row label="Provider" description="Insurance company providing the coverage.">
                    <Input size="sm" value={provider} onChange={(e) => setProvider(e.target.value)} />
                </Row>
                <Row label="Claim Number" description="Insurance claim number.">
                    <Input size="sm" value={claim} onChange={(e) => setClaim(e.target.value)} />
                </Row>
                <Row label="Policy Number" description="Insurance policy number.">
                    <Input size="sm" value={policy} onChange={(e) => setPolicy(e.target.value)} />
                </Row>
                <Row label="Agent Contact" description="Insurance agent contact number.">
                    <Input size="sm" value={agentContact} onChange={(e) => setAgentContact(e.target.value)} />
                </Row>
                <Row label="Agent Number" description="Assigned agent phone number.">
                    <Input size="sm" value={agentNumber} onChange={(e) => setAgentNumber(e.target.value)} />
                </Row>
                <Row label="Agent Email" description="Assigned agent email address.">
                    <Input size="sm" type="email" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} />
                </Row>
                <Row label="Adjuster Contact" description="Insurance adjuster contact number.">
                    <Input size="sm" value={adjusterContact} onChange={(e) => setAdjusterContact(e.target.value)} />
                </Row>
                <Row label="Adjuster Email" description="Insurance adjuster email address.">
                    <Input
                        size="sm"
                        type="email"
                        value={adjusterEmail}
                        onChange={(e) => setAdjusterEmail(e.target.value)}
                    />
                </Row>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

function Row({ label, description, children }: { label: string; description: string; children: any }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ flexBasis: 300 }}>
                <Typography level="title-sm">{label}</Typography>
                <Typography level="body-sm">{description}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
    );
}
